<template>
    <div class="mb-3">
        <v-row v-if="importer.processing || importer.processingStartList">
            <v-col cols="12">
                <v-progress-linear
                    v-if="importer.processing"
                    :size="100"
                    :width="15"
                    :height="25"
                    :value="importer.progress"
                    color="primary"
                    rounded
                >
                    {{ importer.progress }}%
                </v-progress-linear>

                <v-alert v-if="importer.errors" type="error" dismissible>
                    <ul>
                        <li v-for="(error, index) in importer.errors" :key="index">{{ error }}</li>
                    </ul>
                </v-alert>

                <v-progress-linear
                    v-if="importer.processingStartList"
                    indeterminate
                    :size="100"
                    :width="15"
                    :height="25"
                    color="primary"
                    rounded
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <div class="grid">
                    <div v-if="showBackLink" class="grid-item" style="justify-self: left">
                        <inertia-link as="v-btn" :href="`/meets/${meet.slug}`" text class="px-0">
                            <v-icon left>mdi-chevron-left</v-icon> Back
                        </inertia-link>
                    </div>

                    <transition name="fade">
                        <div class="grid-item" style="justify-self: right">
                            <v-menu ref="menu" offset-y nudge-left="100">
                                <template #activator="{ on }">
                                    <v-btn :icon="!canModify" v-on="on">
                                        <v-icon>mdi-cog</v-icon>
                                        <span v-if="canModify" class="ml-2">Settings</span>
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item
                                        v-if="canModify"
                                        class="font-weight-bold"
                                        :href="`/meets/${meet.id}/edit`"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-pen</v-icon>
                                        </v-list-item-icon>
                                        Edit
                                    </v-list-item>

                                    <!-- Announcements -->
                                    <v-list-item
                                        v-if="user?.is_admin"
                                        :href="$route('meets.announcements.index', meet)"
                                        class="font-weight-bold"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-bullhorn</v-icon>
                                        </v-list-item-icon>
                                        Announcements
                                    </v-list-item>

                                    <v-list-item
                                        :href="`/meets/${meetId}/full-results`"
                                        class="font-weight-bold"
                                        two-line
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-podium</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                            <v-list-item-title>Full Results</v-list-item-title>

                                            <v-list-item-subtitle>Updated after every session</v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>

                                    <v-list-item
                                        v-if="!showingSettings"
                                        class="font-weight-bold"
                                        @click.prevent="showingSettings = true"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-eye</v-icon>
                                        </v-list-item-icon>
                                        Show Settings
                                    </v-list-item>

                                    <v-list-item
                                        v-if="showingSettings"
                                        class="font-weight-bold"
                                        @click.prevent="showingSettings = false"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-eye-off</v-icon>
                                        </v-list-item-icon>
                                        Hide Settings
                                    </v-list-item>

                                    <v-list-item class="font-weight-bold" @click.prevent="leaderboardModal = true">
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-presentation</v-icon>
                                        </v-list-item-icon>
                                        Leaderboard
                                    </v-list-item>

                                    <v-list-item
                                        class="font-weight-bold"
                                        @click.prevent="gymChampsLeaderboardModal = true"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-presentation</v-icon>
                                        </v-list-item-icon>
                                        Gym Champs Leaderboard
                                    </v-list-item>

                                    <v-list-item class="font-weight-bold" @click.prevent="scoreFlashModal = true">
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-flash</v-icon>
                                        </v-list-item-icon>
                                        Score Flash
                                    </v-list-item>

                                    <v-list-item
                                        class="font-weight-bold"
                                        @click.prevent="gymChampsScoreFlashModal = true"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-flash</v-icon>
                                        </v-list-item-icon>
                                        Gym Champs Score Flash
                                    </v-list-item>

                                    <v-list-item class="font-weight-bold" @click.prevent="timerModal = true">
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-clock</v-icon>
                                        </v-list-item-icon>
                                        Timer
                                    </v-list-item>

                                    <v-list-item
                                        :href="$route('meets.edit-logos', meetId)"
                                        class="font-weight-bold"
                                        @click.prevent="$inertia.visit($route('meets.edit-logos', meetId))"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-image-multiple</v-icon>
                                        </v-list-item-icon>
                                        Graphics
                                    </v-list-item>

                                    <v-list-item class="font-weight-bold" :href="`/meets/${meet.id}/record`">
                                        <v-list-item-icon class="mr-4">
                                            <v-icon color="red">mdi-record</v-icon>
                                        </v-list-item-icon>
                                        Record
                                    </v-list-item>

                                    <v-list-item class="font-weight-bold" :href="`/meets/${meet.id}/record-manager`">
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-video</v-icon>
                                        </v-list-item-icon>
                                        Recording Manager
                                    </v-list-item>

                                    <verify-results-control :meet="meet">
                                        <template #default="{ handler }">
                                            <v-list-item
                                                class="font-weight-bold"
                                                @click.stop="handleVerifyResultsClicked(handler)"
                                            >
                                                <v-list-item-icon class="mr-4">
                                                    <v-icon>mdi-check-decagram</v-icon>
                                                </v-list-item-icon>
                                                Verify Results
                                            </v-list-item>
                                        </template>
                                    </verify-results-control>

                                    <v-list-item
                                        v-if="canModify"
                                        class="font-weight-bold"
                                        :href="`/meets/${meet.id}/analysis-report`"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-magnify</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title>Analysis Report</v-list-item-title>
                                    </v-list-item>

                                    <v-divider v-if="canModify" />

                                    <v-list-item
                                        v-if="false && canModify && $page.props.app_environment !== 'staging'"
                                        class="font-weight-bold"
                                        @click.prevent="copyToTest"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-test-tube</v-icon>
                                        </v-list-item-icon>
                                        Setup for Testing
                                    </v-list-item>

                                    <v-list-item
                                        v-if="$page.props.app_environment !== 'staging'"
                                        class="font-weight-bold"
                                        :href="`/flyer?meet_id=${meet.id}&colorful=1`"
                                        target="_blank"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-newspaper-variant-multiple</v-icon>
                                        </v-list-item-icon>
                                        Print Flyer
                                    </v-list-item>

                                    <v-list-item
                                        v-if="user?.is_admin"
                                        class="font-weight-bold"
                                        :href="`/push-live?meet_id=${meet.id}`"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon color="yellow">mdi-counter</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="yellow--text">Enter Scores</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item
                                        v-if="false && canModify"
                                        class="font-weight-bold"
                                        :disabled="importer.processing"
                                        @click="importPrelims"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-upload</v-icon>
                                        </v-list-item-icon>
                                        Import Prelims
                                    </v-list-item>

                                    <v-list-item
                                        v-if="false && canModify"
                                        class="font-weight-bold"
                                        :disabled="importer.processing"
                                        @click="importFinals"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-upload</v-icon>
                                        </v-list-item-icon>
                                        Import Finals
                                    </v-list-item>

                                    <v-list-item
                                        v-if="false && canModify"
                                        class="font-weight-bold"
                                        :disabled="importer.processingStartList"
                                        @click="loadStartList"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-counter</v-icon>
                                        </v-list-item-icon>
                                        Import Start List
                                    </v-list-item>

                                    <v-list-item
                                        v-if="canModify && $page.props.route_name === 'result-categories.show'"
                                        class="font-weight-bold"
                                        @click="reprocessResults"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon>mdi-restart-alert</v-icon>
                                        </v-list-item-icon>
                                        Reprocess Results
                                    </v-list-item>

                                    <v-list-item
                                        v-if="canModify"
                                        class="font-weight-bold"
                                        href="#"
                                        @click.stop="resetMeetDialog = true"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon color="red">mdi-restart</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="red--text">Reset Meet</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item
                                        v-if="canModify"
                                        class="font-weight-bold"
                                        href="#"
                                        @click.stop="flushScoreFlashDialog = true"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon color="red">mdi-flash-off</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="red--text">Flush Score Flash</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item
                                        v-if="canModify"
                                        class="font-weight-bold"
                                        href="#"
                                        @click.stop="deleteMeetDialog = true"
                                    >
                                        <v-list-item-icon class="mr-4">
                                            <v-icon color="red">mdi-delete</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="red--text">Delete Meet</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                    </transition>
                </div>
            </v-col>
        </v-row>

        <v-row v-if="!latestResult">
            <v-col cols="12" class="text-center">
                <div class="grid">
                    <div class="grid-item">
                        <template v-if="meet.is_international">
                            <div class="mx-auto mb-4 d-flex">
                                <div>
                                    <meet-logo v-if="meet.logo" :path="meet.logo" />
                                </div>
                                <div>
                                    <meet-logo path="/images/FIG.png" alt="FIG" />
                                </div>
                            </div>
                        </template>
                        <meet-logo v-else-if="meet.logo" :path="meet.logo" class="mx-auto mb-4" />

                        <h5 v-if="!noTitle" class="text-h5">
                            <inertia-link :href="`/meets/${meet.id}`">{{ meet.title }}</inertia-link>
                        </h5>
                        <div class="text-h5">
                            {{ meet.date_range }}
                        </div>
                        <div class="text-h6">
                            {{ meet.location }}
                        </div>
                        <div v-if="meet.is_free_to_access">
                            <v-chip class="mt-2" color="green lighten-1" dark small> Free </v-chip>
                        </div>
                        <div v-if="isSelectionMeet" class="mt-2">
                            <inertia-link
                                as="v-btn"
                                href="/rankings"
                                text
                                large
                                class="font-weight-bold blue-grey--text"
                            >
                                <v-icon class="mr-2">mdi-trophy</v-icon>
                                Selection Rankings
                            </inertia-link>
                        </div>
                        <div v-if="meet.id === 24" class="text-subtitle-1 mt-2">
                            <inertia-link href="/rankings"> Elite Selection Rankings </inertia-link>
                        </div>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-row v-if="liveStreams.length > 0">
            <v-col cols="12" class="mx-auto text-center">
                <v-btn
                    v-for="(liveStream, index) in liveStreams"
                    :key="liveStream.id"
                    target="_blank"
                    class="white--text mx-1"
                    color="red"
                    outlined
                    @click="playingLiveStreamId = index"
                >
                    <v-icon left>mdi-video</v-icon>

                    {{ liveStream.title }}
                </v-btn>
            </v-col>
        </v-row>

        <v-row v-if="meet.block_schedule || meet.training_schedule">
            <v-col cols="12" class="mx-auto text-center">
                <v-btn
                    v-if="meet.block_schedule"
                    target="_blank"
                    class="white--text mx-1"
                    color="primary"
                    outlined
                    small
                    :href="meet.block_schedule_url"
                >
                    <v-icon small class="mr-2">mdi-calendar</v-icon>
                    Block Schedule
                </v-btn>

                <v-btn
                    v-if="meet.training_schedule"
                    target="_blank"
                    class="white--text mx-1"
                    color="primary"
                    outlined
                    small
                    :href="meet.training_schedule_url"
                >
                    <v-icon small class="mr-2">mdi-timer</v-icon>
                    Training Schedule
                </v-btn>
            </v-col>
        </v-row>

        <v-row v-if="$page.props.spreadsheets">
            <v-col cols="12" class="mx-auto text-center">
                <v-btn
                    v-for="spreadsheet in $page.props.spreadsheets"
                    :key="spreadsheet.id"
                    class="white--text mx-1"
                    color="primary"
                    outlined
                    small
                    :href="`/meets/${meetId}/spreadsheets/${spreadsheet.key}`"
                >
                    <v-icon small class="mr-2">mdi-file-excel</v-icon>
                    {{ spreadsheet.title }}
                </v-btn>
            </v-col>
        </v-row>

        <v-row v-if="playingLiveStreamId !== null">
            <v-col cols="12" class="mx-auto text-center">
                <keep-alive>
                    <div v-html="liveStreams[playingLiveStreamId].embed"></div>
                </keep-alive>
            </v-col>
        </v-row>

        <v-row v-if="meet.timestamp('ends_at').isBefore()">
            <v-col cols="12 text-center">
                <v-btn
                    class="white--text mx-1"
                    color="primary"
                    outlined
                    small
                    :href="$route('meet.full-results', meet.id)"
                >
                    <v-icon small class="mr-2">mdi-podium</v-icon>
                    Full Results
                </v-btn>
            </v-col>
        </v-row>

        <v-row v-if="showingSettings">
            <v-col cols="12" class="mx-auto">
                <v-row>
                    <v-col cols="12">
                        <hr />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="10" offset="1" sm="3">
                        <div class="mx-auto">
                            <h4>Tie Break</h4>
                        </div>
                        <v-list>
                            <meet-header-setting :setting="meet.break_jo_ties_prelims" label="Developmental Prelims" />
                            <meet-header-setting :setting="meet.break_jo_ties_finals" label="Developmental Finals" />
                            <meet-header-setting :setting="meet.break_elite_ties_prelims" label="Elite Prelims" />
                            <meet-header-setting :setting="meet.break_elite_ties_finals" label="Elite Finals" />
                        </v-list>
                    </v-col>

                    <v-col cols="10" offset="1" sm="3">
                        <h4>Qualifications</h4>
                        <v-list>
                            <meet-header-setting
                                :setting="meet.jo_qualification_meet"
                                label="Developmental National Qualifier"
                            />
                            <meet-header-setting
                                :setting="meet.elite_qualification_meet"
                                label="Elite National Qualifier"
                            />
                            <meet-header-setting :setting="meet.elite_mobility" label="Elite Mobility" />
                        </v-list>
                    </v-col>

                    <v-col cols="10" offset="1" sm="3">
                        <h4>Final Score Calculation</h4>

                        <h5 class="mt-2">Developmental:</h5>
                        <v-chip v-if="!meet.jo_new_life_finals" small>Cumulative</v-chip>
                        <v-chip v-else color="green" text-color="white" small>Start from zero</v-chip>

                        <h5 class="mt-2">Elite:</h5>
                        <v-chip v-if="!meet.elite_new_life_finals" small>Cumulative</v-chip>
                        <v-chip v-else color="green" text-color="white" small>Start from zero</v-chip>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <v-row v-if="showingSettings && canModify">
            <v-col xs="10" sm="10" md="10" lg="6" offset-xs="1" offset-sm="1" offset-md="1" offset-lg="3">
                <v-alert color="primary" dark icon="mdi-key" border="left" prominent style="overflow-x: auto">
                    <strong>Meet Token:</strong>
                    <br />{{ meet.token }}
                    <v-tooltip bottom>
                        <template #activator="{ on }">
                            <v-btn icon class="ml-4" v-on="on" @click="copyMeetToken">
                                <v-icon>mdi-content-copy</v-icon>
                            </v-btn>
                        </template>
                        <span>Copy to clipboard</span>
                    </v-tooltip>
                </v-alert>
            </v-col>
        </v-row>

        <transition name="fade" mode="out-in">
            <v-row v-if="latestResult" :key="latestResult.id">
                <v-col cols="12">
                    <score-highlight :round="round !== 'start_list' ? round : 'prelims'" :result="latestResult" />
                </v-col>
            </v-row>
        </transition>

        <advertisements v-if="advertisements.length > 0" :ads="advertisements" />

        <input ref="importInput" type="file" style="display: none" @change="importResults" />
        <input ref="importStartListInput" type="file" style="display: none" @change="importStartList" />

        <v-dialog v-model="leaderboardModal" transition="dialog-top-transition" width="500">
            <meet-leaderboard-modal :meet="meet" />
        </v-dialog>

        <v-dialog v-model="gymChampsLeaderboardModal" transition="dialog-top-transition" width="500">
            <gym-champs-meet-leaderboard-modal :meet="meet" />
        </v-dialog>

        <v-dialog v-model="scoreFlashModal" transition="dialog-top-transition" width="600">
            <meet-score-flash-modal :meet="meet" />
        </v-dialog>

        <v-dialog v-model="gymChampsScoreFlashModal" transition="dialog-top-transition" width="600">
            <gym-champs-score-flash-modal :meet="meet" />
        </v-dialog>

        <v-dialog v-model="timerModal" transition="dialog-top-transition" width="700">
            <timer-modal :meet="meet" />
        </v-dialog>

        <v-dialog v-model="resetMeetDialog" transition="dialog-top-transition" width="500">
            <v-card>
                <v-card-title class="text-h5"> Reset Meet </v-card-title>

                <v-card-text>
                    Are you sure you wish to reset this meet? This will destroy all results and is irreversible.
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn color="grey lighten-1" text @click.stop="resetMeetDialog = false">Cancel</v-btn>
                    <v-btn color="red lighten-2" text @click="resetMeet"> Reset Meet </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="deleteMeetDialog" transition="dialog-top-transition" width="500">
            <v-card>
                <v-card-title class="text-h5"> Delete Meet </v-card-title>

                <v-card-text>
                    Are you sure you wish to delete this meet? The meet will only be recoverable by contacting support.
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn color="grey lighten-1" text @click.stop="deleteMeetDialog = false">Cancel</v-btn>
                    <v-btn color="red lighten-2" text @click="deleteMeet"> Delete Meet </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="flushScoreFlashDialog" transition="dialog-top-transition" width="500">
            <v-card>
                <v-card-title class="text-h5"> Flush the Score Flash </v-card-title>

                <v-card-text>
                    Are you sure you wish to flush the score flash? This will set all scores to flashed, forcing the
                    score flash to wait for new scores.
                </v-card-text>

                <v-card-actions>
                    <v-spacer />
                    <v-btn color="grey lighten-1" text @click.stop="flushScoreFlashDialog = false">Cancel</v-btn>
                    <v-btn :loading="flushingScoreFlash" color="red lighten-2" text @click="flushScoreFlash">
                        Flush
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { VBtn } from 'vuetify/lib';
import Meet from '@/Models/Meet.js';
import MeetHeaderSetting from '@/Components/MeetHeaderSetting.vue';
import MeetLogo from '@/Components/MeetLogo.vue';
import Result from '@/Models/Result.js';
import ScoreHighlight from '@/Components/ScoreHighlight/ScoreHighlight.vue';
import ImporterMixin from '@/Mixins/Importer.mixin.js';
import Advertisements from '@/Components/Advertisements/Advertisements.vue';
import MeetLeaderboardModal from '@/Components/Modals/MeetLeaderboardModal.vue';
import GymChampsMeetLeaderboardModal from '@/Components/Modals/GymChampsMeetLeaderboardModal.vue';
import backgroundColors from '@/background-colors.js';
import MeetScoreFlashModal from '@/Components/Modals/MeetScoreFlashModal.vue';
import GymChampsScoreFlashModal from '@/Components/Modals/GymChampsScoreFlashModal.vue';
import User from '@/Models/User.js';
import TimerModal from '@/Components/Modals/TimerModal.vue';
import VerifyResultsControl from '@/Components/VerifyResultsControl.vue';
import AllAroundResult from '@/Models/AllAroundResult.js';

export default {
    name: 'MeetHeader',

    components: {
        VerifyResultsControl,
        TimerModal,
        MeetScoreFlashModal,
        GymChampsScoreFlashModal,
        MeetLeaderboardModal,
        GymChampsMeetLeaderboardModal,
        Advertisements,
        ScoreHighlight,
        MeetLogo,
        MeetHeaderSetting,
        VBtn,
    },

    mixins: [ImporterMixin],

    props: {
        meetId: {
            type: Number,
            required: true,
        },

        advertisements: {
            type: Array,
            required: false,
            default: () => [],
        },

        liveStreams: {
            type: Array,
            required: false,
            default: () => [],
        },

        noTitle: {
            type: Boolean,
            required: false,
            default: false,
        },

        showBackLink: {
            type: Boolean,
            required: false,
            default: false,
        },

        latestResult: {
            type: [Result, AllAroundResult, null],
            required: false,
            default: null,
        },

        hadFinals: {
            type: Boolean,
            required: false,
            default: false,
        },

        round: {
            type: String,
            required: false,
            default: 'prelims',
        },

        isSelectionMeet: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    data: () => ({
        leaderboardModal: false,
        scoreFlashModal: false,
        gymChampsLeaderboardModal: false,
        gymChampsScoreFlashModal: false,
        timerModal: false,
        resetMeetDialog: false,
        deleteMeetDialog: false,
        showingSettings: false,
        flushScoreFlashDialog: false,
        flushingScoreFlash: false,
        playingLiveStreamId: null,
    }),

    computed: {
        meet() {
            return Meet.find(this.meetId);
        },

        user() {
            return User.find(this.$page.props.auth.user?.id);
        },

        canModify() {
            return (
                this.$page.props.auth.user &&
                (this.$page.props.auth.user.id === this.meet.created_by ||
                    this.$page.props.auth.user.id === this.meet.host_id ||
                    this.user.is_admin)
            );
        },
    },

    created() {
        this.importer.meet_id = this.meetId;
    },

    methods: {
        copyToTest() {
            this.$inertia.post(`/meets/${this.meet.id}/copy-to-test`);
        },

        resetMeet() {
            this.$inertia.delete(`/meets/${this.meetId}/reset`, {
                onFinish() {
                    this.resetMeetDialog = false;
                },
            });

            this.resetMeetDialog = false;
        },

        deleteMeet() {
            this.$inertia.delete(`/meets/${this.meetId}`, {
                onFinish: () => {
                    this.deleteMeetDialog = false;

                    Meet.delete(this.meetId);
                },
            });
        },

        reprocessResults() {
            this.$inertia.post(`/meets/${this.meetId}/reprocess-results`, {
                result_category_id: this.$page.props.resultCategoryId,
                round: this.round,
            });
        },

        copyMeetToken() {
            const el = document.createElement('textarea');
            el.value = this.meet.token;
            el.setAttribute('readonly', '');
            el.style.position = 'absolute';
            el.style.left = '-9999px';
            document.body.appendChild(el);
            const selected = document.getSelection().rangeCount > 0 ? document.getSelection().getRangeAt(0) : false;
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            if (selected) {
                document.getSelection().removeAllRanges();
                document.getSelection().addRange(selected);
            }
        },

        importPrelims() {
            this.importer.round = 'prelims';
            this.$refs.importInput.click();
        },

        importFinals() {
            this.importer.round = 'finals';
            this.$refs.importInput.click();
        },

        loadStartList() {
            this.importer.round = 'prelims';
            this.$refs.importStartListInput.click();
        },

        scoreFlash() {
            let url = `/meets/${this.meet.id}/score-flash/live?dark=true&background_type=gradient`;

            backgroundColors.forEach(([startColor, endColor]) => {
                url += `&background_colors[]=${encodeURIComponent(`${startColor},${endColor}`)}`;
            });

            window.open(url, '_blank');
        },

        async flushScoreFlash() {
            this.flushingScoreFlash = true;

            await axios.delete(`/api/meets/${this.meet.id}/score-flash`);

            this.flushingScoreFlash = false;

            this.flushScoreFlashDialog = false;
        },

        handleVerifyResultsClicked(internalHandler) {
            internalHandler();

            if (this.$store.state.verifiableMeetIds === '*') {
                this.$refs.menu.isActive = false;
            }
        },
    },
};
</script>

<style lang="css" scoped>
.grid {
    display: grid;
}

.grid-item {
    display: inline-block;
    grid-column-start: 1;
    grid-row-start: 1;
    justify-self: center;
}

hr {
    border: 0;
    height: 1px;
    background: #aaa linear-gradient(to right, #fff, #aaa, #fff);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 1s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
}
</style>
